import React, { useState, useEffect, useContext } from "react";
import { Modal, TextField, Button } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { ForwardMessageContext } from "../../context/ForwardingMessage/ForwardingMessageContext";
import useContacts from "../../hooks/useContacts"; // <-- IMPORTAMOS O HOOK
import api from "../../services/api";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import { i18n } from "../../translate/i18n";

const baseModalStyle = {
  position: "absolute",
  top: "50%", left: "50%",
  transform: "translate(-50%, -50%)",
  padding: "20px",
  outline: "none"
};

const ForwardMessageModal = ({ open, onClose }) => {
  const theme = useTheme();
  const { 
    selectedForwardMessages,
    clearForwardSelection
  } = useContext(ForwardMessageContext);

  // Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: adaptar o fundo do modal ao tema escuro/claro.
  const modalStyle = {
    ...baseModalStyle,
    background: theme.palette.background.paper,
    color: theme.palette.text.primary,
    borderRadius: 8,
    minWidth: 340,
    maxWidth: "90vw",
    boxShadow:
      theme.palette.type === "dark"
        ? "0 8px 24px rgba(0, 0, 0, 0.6)"
        : "0 6px 18px rgba(0, 0, 0, 0.2)",
    border:
      theme.palette.type === "dark"
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(0, 0, 0, 0.08)",
  };

  // Texto que será usado como "searchParam" no hook
  const [searchParam, setSearchParam] = useState("");
  // Se quiser paginação, crie um estado para pageNumber
  const [pageNumber, setPageNumber] = useState(1);

  // AQUI USAMOS O HOOK do Whaticket:
  // Ele já faz a requisição: GET /contacts?searchParam=...&pageNumber=...
  const { contacts, loading, hasMore, count } = useContacts({
    searchParam,
    pageNumber
  });

  // Estado para armazenar o contato selecionado no Autocomplete
  const [selectedContact, setSelectedContact] = useState(null);
  const [isSending, setIsSending] = useState(false);

  // Ao abrir o modal, limpamos
  useEffect(() => {
    if (open) {
      setSearchParam("");
      setSelectedContact(null);
      setPageNumber(1); // retorna para a página 1
      setIsSending(false);
    }
  }, [open]);

  const toSortableDateValue = (message) => {
    const timestamp = Number(message?.timestamp || 0);
    if (Number.isFinite(timestamp) && timestamp > 0) {
      return timestamp >= 1e12 ? timestamp : timestamp * 1000;
    }

    const createdAt = new Date(message?.createdAt || 0).getTime();
    if (Number.isFinite(createdAt) && createdAt > 0) {
      return createdAt;
    }

    return Number.MAX_SAFE_INTEGER;
  };

  const orderedMessages = [...selectedForwardMessages].sort((a, b) => {
    const byDate = toSortableDateValue(a) - toSortableDateValue(b);
    if (byDate !== 0) {
      return byDate;
    }

    return Number(a?.id || 0) - Number(b?.id || 0);
  });

  // Quando apertar "Encaminhar"
  const handleForward = async () => {
    if (!selectedContact || orderedMessages.length === 0 || isSending) return;

    setIsSending(true);

    try {
      let sentCount = 0;

      for (const message of orderedMessages) {
        await api.post("/messagefwd/forward", {
          messageId: message.id,
          contactId: selectedContact.id
        });
        sentCount += 1;
      }

      toast.success(
        orderedMessages.length === 1
          ? i18n.t("forwardMessageModal.successSingle", { name: selectedContact.name })
          : i18n.t("forwardMessageModal.successMultiple", { count: sentCount, name: selectedContact.name }),
        { autoClose: 3000 }
      );

      clearForwardSelection();
      onClose();
    } catch (err) {
      const code = err?.response?.data?.error;
      if (code === "ERR_CLOUDAPI_WINDOW_CLOSED") {
        toast.error(
          i18n.t("forwardMessageModal.windowClosed", { name: selectedContact?.name || "" }),
          { autoClose: 6000 }
        );
      } else {
        toast.error(i18n.t("forwardMessageModal.stoppedOnError"), { autoClose: 4000 });
        toastError(err);
      }
    } finally {
      setIsSending(false);
    }
  };

  if (!open) return null;

  // Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: evitar exibicao de payload bruto (base64)
  // no modal de encaminhamento para mensagens de localizacao/midia.
  const getForwardPreviewText = (message) => {
    if (!message) return "";

    const mediaType = String(message.mediaType || "").toLowerCase();
    const rawBody = String(message.body || "").trim();

    const extractNameFromVcard = (vcard) => {
      const match = String(vcard || "").match(/FN[^:]*:(.+)/i);
      return match?.[1]?.trim() || "";
    };

    const extractNumberFromVcard = (vcard) => {
      const waidMatch = String(vcard || "").match(/waid=(\d+)/i);
      if (waidMatch?.[1]) {
        return waidMatch[1];
      }

      const telMatch = String(vcard || "").match(/TEL[^:]*:([^\n\r]+)/i);
      return telMatch?.[1]?.replace(/[^\d+]/g, "") || "";
    };

    // Autor: GitHub Copilot | Data: 28/03/2026 | Motivo: mostrar preview amigavel para contato
    // no modal e evitar exibicao do bloco BEGIN:VCARD bruto.
    if (mediaType === "contactmessage" || mediaType === "vcard") {
      const contactName = extractNameFromVcard(rawBody);
      const contactNumber = extractNumberFromVcard(rawBody);

      if (contactName && contactNumber) {
        return i18n.t("forwardMessageModal.previewContactWithNumber", {
          name: contactName,
          number: contactNumber,
        });
      }

      if (contactName) {
        return i18n.t("forwardMessageModal.previewContactWithName", {
          name: contactName,
        });
      }

      if (contactNumber) {
        return i18n.t("forwardMessageModal.previewContactWithNumberOnly", {
          number: contactNumber,
        });
      }

      return i18n.t("forwardMessageModal.previewSharedContact");
    }

    if (mediaType === "locationmessage" || mediaType === "livelocationmessage") {
      const parts = rawBody.split("|");
      const description = String(parts.slice(2).join("|") || "").trim();
      const isCoordinates = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/i.test(description);
      return isCoordinates || !description ? i18n.t("forwardMessageModal.previewSharedLocation") : description;
    }

    if (message.mediaUrl && mediaType && mediaType !== "chat") {
      return i18n.t("forwardMessageModal.previewMedia", {
        mediaType: message.mediaType || i18n.t("forwardMessageModal.previewFile"),
      });
    }

    return rawBody;
  };

  const previewMessage = orderedMessages[0] || null;
  const previewText = getForwardPreviewText(previewMessage);

  return (
    <Modal open={open} onClose={onClose}>
      <div style={modalStyle}>
        <h2>{i18n.t("forwardMessageModal.title")}</h2>
        <p>
          {i18n.t("forwardMessageModal.selectedCount", { count: orderedMessages.length })}
          {orderedMessages.length > 0 ? ` | ${i18n.t("forwardMessageModal.previewPrefix")}: ${previewText}` : ""}
        </p>

        <Autocomplete
          style={{ marginBottom: 16, width: 300 }}
          // "contacts" vem do hook useContacts
          options={contacts}
          getOptionLabel={(option) => option.number ? `${option.name} - ${option.number}` : option.name}
          renderOption={(option) => (
            <>
              <span style={{ fontSize: 14 }}>
                {option.name} {option.number && `- ${option.number}`}
              </span>
            </>
          )}
          value={selectedContact}
          loading={loading}
          onChange={(_event, newValue) => {
            setSelectedContact(newValue);
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={i18n.t("forwardMessageModal.searchContactLabel")}
              variant="outlined"
              onChange={(e) => {
                // A cada digitação, atualiza searchParam,
                // o hook useContacts faz a busca (com debounce de 500ms)
                setSearchParam(e.target.value);
                setPageNumber(1);
              }}
            />
          )}
        />

        {/* Exemplo de "Carregar mais" se quiser paginação manual */}
        {hasMore && (
          <Button
            onClick={() => setPageNumber((prev) => prev + 1)}
            variant="outlined"
            disabled={loading}
            style={{ marginBottom: 16 }}
          >
            {i18n.t("forwardMessageModal.loadMoreContacts")}
          </Button>
        )}

        <div>
          <Button
            variant="contained"
            color="primary"
            onClick={handleForward}
            disabled={!selectedContact || orderedMessages.length === 0 || isSending}
          >
            {isSending ? i18n.t("forwardMessageModal.forwarding") : i18n.t("forwardMessageModal.forward")}
          </Button>
          &nbsp;
          <Button variant="outlined" onClick={onClose} disabled={isSending}>
            {i18n.t("forwardMessageModal.cancel")}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ForwardMessageModal;
